import React, { useState, useEffect } from 'react';
import { getInvoiceTemplates, setDefaultInvoiceTemplate, previewInvoiceTemplate } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Palette, Check, Eye, Loader2, ArrowRight, Sparkles } from 'lucide-react';

const LAYOUT_LABEL = {
  classic: 'Clásico',
  modern: 'Moderno',
  minimal: 'Minimalista',
  professional: 'Profesional',
};

const InvoiceDesignBanner = ({ entityLabel = 'factura' }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null); // id de la plantilla en preview

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await getInvoiceTemplates();
      setTemplates(data || []);
    } catch {
      // silent: don't block invoices page if templates fail
    } finally {
      setLoading(false);
    }
  };

  const current = templates.find(t => t.is_default) || templates[0];

  const choose = async (tpl) => {
    if (tpl.is_default) { setOpen(false); return; }
    setSaving(true);
    try {
      await setDefaultInvoiceTemplate(tpl.id);
      toast.success(`Diseño "${tpl.name}" activo en tus ${entityLabel}s`);
      await load();
      setOpen(false);
    } catch {
      toast.error('No se pudo activar la plantilla');
    } finally {
      setSaving(false);
    }
  };

  const preview = async (tpl) => {
    setPreviewLoading(tpl.id);
    try {
      const { data } = await previewInvoiceTemplate(tpl.id);
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(url);
    } catch {
      toast.error('Error al generar vista previa');
    } finally {
      setPreviewLoading(null);
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
  };

  if (loading || !current) return null;

  return (
    <>
      <div
        className="flex items-center gap-4 p-4 bg-gradient-to-r from-violet-50 via-white to-orange-50/40 border border-violet-100 rounded-2xl"
        data-testid="invoice-design-banner"
      >
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: `linear-gradient(135deg, ${current.primary_color}, ${current.accent_color || current.secondary_color})` }}
        >
          <Palette className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 leading-none">Diseño de {entityLabel} activo</p>
          <h3 className="font-semibold text-stone-900 mt-1 truncate">{current.name}</h3>
          <p className="text-xs text-stone-500 mt-0.5">{LAYOUT_LABEL[current.layout] || current.layout} · Se aplica a todos los PDFs (salvo override por documento o cliente)</p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {[current.primary_color, current.secondary_color, current.accent_color].filter(Boolean).map((c, i) => (
            <div key={i} className="w-4 h-12 rounded-full" style={{ background: c }} />
          ))}
        </div>
        <Button
          onClick={() => setOpen(true)}
          variant="outline"
          className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-100 flex-shrink-0"
          data-testid="change-invoice-design-button"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Cambiar diseño
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Selector Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Elige el diseño de tus {entityLabel}s</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500 -mt-2">Selecciona la plantilla que se usará por defecto en todos tus PDFs. Puedes cambiarla cuando quieras.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {templates.map(tpl => {
              const active = !!tpl.is_default;
              return (
                <div
                  key={tpl.id}
                  className={`relative bg-white rounded-2xl border-2 p-4 transition-all duration-200 cursor-pointer hover:-translate-y-0.5 ${
                    active ? 'border-[#FF6B4A] shadow-lg shadow-orange-100' : 'border-stone-200 hover:border-stone-300'
                  }`}
                  onClick={() => choose(tpl)}
                  data-testid={`pick-template-${tpl.id}`}
                >
                  {active && (
                    <div className="absolute top-2 right-2 w-7 h-7 bg-[#FF6B4A] rounded-full flex items-center justify-center shadow-md">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {/* Mini preview */}
                  <div className="aspect-[3/4] rounded-lg overflow-hidden border border-stone-100 bg-white mb-3 p-2 text-[6px] leading-snug">
                    <div className="w-full h-2 rounded mb-1" style={{ background: tpl.primary_color }} />
                    <div className="text-stone-900 font-bold mb-1" style={{ color: tpl.primary_color, fontSize: '8px' }}>{tpl.title_label || 'FACTURA'}</div>
                    <div className="text-stone-500">Nº EJEMPLO-001</div>
                    <div className="mt-1 grid grid-cols-2 gap-1">
                      <div className="text-stone-700">De: Mi Empresa</div>
                      <div className="text-stone-700">Para: Cliente</div>
                    </div>
                    <div className="mt-1.5 rounded" style={{ background: tpl.primary_color, height: '6px' }} />
                    <div className="mt-0.5 border-b border-stone-200 py-0.5 px-0.5 grid grid-cols-4 gap-0.5 text-stone-700">
                      <span>Servicio</span><span>1</span><span>50€</span><span>50€</span>
                    </div>
                    <div className="mt-0.5 border-b border-stone-200 py-0.5 px-0.5 grid grid-cols-4 gap-0.5 text-stone-700">
                      <span>Mant.</span><span>2</span><span>30€</span><span>60€</span>
                    </div>
                    <div className="mt-1 text-right font-bold" style={{ color: tpl.primary_color, fontSize: '7px' }}>TOTAL: 133,10€</div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-stone-900 truncate">{tpl.name}</h4>
                    <p className="text-[11px] text-stone-500">{LAYOUT_LABEL[tpl.layout] || tpl.layout} · {tpl.font_family}</p>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: tpl.primary_color }} />
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: tpl.secondary_color }} />
                    <div className="flex-1 h-1.5 rounded-full" style={{ background: tpl.accent_color }} />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); preview(tpl); }}
                      className="rounded-lg flex-1 h-8 text-xs"
                      disabled={previewLoading === tpl.id}
                      data-testid={`preview-template-${tpl.id}`}
                    >
                      {previewLoading === tpl.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><Eye className="w-3 h-3 mr-1" />Ver PDF</>)}
                    </Button>
                    {!active && (
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); choose(tpl); }}
                        disabled={saving}
                        className="rounded-lg flex-1 h-8 text-xs bg-[#FF6B4A] hover:bg-[#E05A3E]"
                        data-testid={`activate-template-${tpl.id}`}
                      >
                        Activar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-stone-500 text-center pt-2">
            ¿Quieres personalizar colores, logo o pie de página? Ve a <a href="/templates" className="text-[#FF6B4A] font-medium hover:underline">Plantillas factura</a>.
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF preview dialog */}
      <Dialog open={!!previewBlobUrl} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Vista previa del PDF</DialogTitle>
          </DialogHeader>
          <div className="flex-1 mt-4 bg-stone-100 rounded-xl overflow-hidden">
            {previewBlobUrl && <iframe src={previewBlobUrl} className="w-full h-full" title="Vista previa" />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvoiceDesignBanner;
